/**
 * RotationDial — circular angle picker with a dragging handle.
 *
 * Primary control for the ButtonItem `rotation` field. Renders a small
 * ~64px SVG dial with 8 tick marks at every 45° (0/45/90/135/180/225/
 * 270/315), a filled handle on the outer ring, and a numeric readout
 * underneath.
 *
 * Interaction:
 *  - Pointer-drag on the handle (or anywhere on the ring): the angle
 *    from the dial's center to the pointer becomes the new rotation.
 *  - Hard 45° snap — every commit rounds to the nearest snap point.
 *    No in-between values, no shift escape (rotation is discrete by
 *    product decision 2026-07-03).
 *  - Click the readout text to open an inline number input (also
 *    snaps to 45° on commit).
 *
 * Secondary slider (below the dial): drag along the 0-360 range for
 * authors who prefer linear input. Snap and Shift-modifier apply here
 * too. The slider mirrors the dial value bidirectionally.
 *
 * 0° = up (12 o'clock). Increasing = clockwise. Matches how "rotate the
 * arrow to point down = 180°" reads.
 */

import { createSignal, For } from 'solid-js';

interface RotationDialProps {
  value: number;
  onChange: (deg: number) => void;
}

/** Snap points every 45°. Hard-snap — no in-between values. */
const SNAP_STEP = 45;

/** Normalize any degree value into [0, 360). */
function norm(deg: number): number {
  const n = deg % 360;
  return n < 0 ? n + 360 : n;
}

/** Hard-snap to nearest 45°. */
function snap(deg: number): number {
  const n = norm(deg);
  return norm(Math.round(n / SNAP_STEP) * SNAP_STEP);
}

/** Convert a (cx, cy) → (px, py) pointer position into an angle in
 *  degrees, with 0° = up (12 o'clock) and increasing clockwise. */
function angleFromPointer(cx: number, cy: number, px: number, py: number): number {
  const dx = px - cx;
  const dy = py - cy;
  // atan2 returns radians from east, counter-clockwise. Convert to
  // degrees-from-north-clockwise: rotate axes so 0 = up, then flip sign.
  const rad = Math.atan2(dx, -dy);
  return norm((rad * 180) / Math.PI);
}

export function RotationDial(props: RotationDialProps) {
  let svgRef: SVGSVGElement | undefined;
  const [editingText, setEditingText] = createSignal(false);
  const [textDraft, setTextDraft] = createSignal('');

  // Handle drag: works on the whole SVG, so grabbing anywhere in the ring
  // starts a drag. Only primary button.
  function onSvgPointerDown(ev: PointerEvent) {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return;
    if (!svgRef) return;
    ev.preventDefault();
    ev.stopPropagation();
    const target = ev.currentTarget as SVGSVGElement;
    target.setPointerCapture(ev.pointerId);
    const rect = svgRef.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    const commit = (e: PointerEvent) => {
      const raw = angleFromPointer(cx, cy, e.clientX, e.clientY);
      props.onChange(snap(raw));
    };
    commit(ev);
    const onMove = (e: PointerEvent) => commit(e);
    const onUp = () => {
      try { target.releasePointerCapture(ev.pointerId); } catch { /* noop */ }
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  function onSliderInput(ev: Event & { currentTarget: HTMLInputElement }) {
    const raw = parseInt(ev.currentTarget.value, 10);
    if (!Number.isFinite(raw)) return;
    props.onChange(snap(raw));
  }

  function startTextEdit() {
    setTextDraft(String(props.value));
    setEditingText(true);
  }
  function commitTextEdit() {
    const parsed = parseInt(textDraft(), 10);
    if (Number.isFinite(parsed)) props.onChange(snap(parsed));
    setEditingText(false);
  }

  // Geometry. 64px SVG viewBox 0..100 for easy math.
  const CX = 50;
  const CY = 50;
  const R_RING = 42;
  const R_HANDLE = 6;
  const TICKS = [0, 45, 90, 135, 180, 225, 270, 315];

  // Position of the handle: current angle projected onto the ring.
  const handlePos = () => {
    const deg = props.value;
    const rad = ((deg - 90) * Math.PI) / 180; // -90 so 0° sits at top
    return {
      x: CX + R_RING * Math.cos(rad),
      y: CY + R_RING * Math.sin(rad),
    };
  };

  return (
    <div class="rotation-dial">
      <svg
        ref={svgRef}
        class="rotation-dial-svg"
        viewBox="0 0 100 100"
        width="64"
        height="64"
        onPointerDown={onSvgPointerDown}
        role="slider"
        aria-label="Rotation"
        aria-valuemin={0}
        aria-valuemax={359}
        aria-valuenow={props.value}
      >
        {/* Outer ring */}
        <circle cx={CX} cy={CY} r={R_RING} class="rotation-dial-ring" />
        {/* Snap ticks */}
        <For each={TICKS}>
          {(t) => {
            const rad = ((t - 90) * Math.PI) / 180;
            const inner = R_RING - 6;
            const outer = R_RING - 2;
            const x1 = CX + inner * Math.cos(rad);
            const y1 = CY + inner * Math.sin(rad);
            const x2 = CX + outer * Math.cos(rad);
            const y2 = CY + outer * Math.sin(rad);
            return (
              <line
                x1={x1} y1={y1} x2={x2} y2={y2}
                class="rotation-dial-tick"
                data-cardinal={t % 90 === 0 ? 'true' : 'false'}
              />
            );
          }}
        </For>
        {/* Pointer line: center → handle */}
        <line
          x1={CX} y1={CY}
          x2={handlePos().x} y2={handlePos().y}
          class="rotation-dial-pointer"
        />
        {/* Handle */}
        <circle
          cx={handlePos().x} cy={handlePos().y} r={R_HANDLE}
          class="rotation-dial-handle"
        />
      </svg>

      <input
        type="range"
        class="rotation-dial-slider"
        min="0" max="315" step="45"
        value={props.value}
        onInput={onSliderInput}
        aria-label="Rotation slider"
      />

      {editingText() ? (
        <input
          type="number"
          class="rotation-dial-input"
          min="0" max="359"
          value={textDraft()}
          onInput={(e) => setTextDraft(e.currentTarget.value)}
          onBlur={commitTextEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.currentTarget.blur(); }
            if (e.key === 'Escape') { setEditingText(false); }
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autofocus
        />
      ) : (
        <button
          type="button"
          class="rotation-dial-readout"
          onClick={startTextEdit}
          title="Click to type an exact angle"
        >
          {props.value}°
        </button>
      )}
    </div>
  );
}
